import { z } from 'zod';

/**
 * stock.ts
 * GET /servicios/stock/balances — LOCX's read-only inventory screen
 * (locx-stock-inventario), backed by servicios' IStockLookupPort (compras'
 * warehouse stock engine, injected via apps/backend/src/main.ts).
 *
 * Rows mirror compras' own StockOnHandRow (@suite/contracts-compras) plus
 * two derived fields GetLocxStockPositionsUseCase computes on top —
 * `ageDays` and the response-level `confidencePercent` — LOCX's own
 * business rules, not part of compras' read model.
 */

/**
 * `ageDays` — days since `lastVerifiedAt`, or since `mostRecentMovementAt`
 * when never counted, or `null` when the position has neither (no cyclic
 * count AND no ledger movement ever recorded for it — the oldest possible
 * bucket, rendered as "sin verificar" rather than a number).
 */
export const LocxStockPositionRowSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  itemMasterId: z.string().uuid(),
  code: z.string().nullable(),
  description: z.string(),
  unit: z.string(),
  qty: z.string(),
  lastVerifiedAt: z.string().nullable(),
  mostRecentMovementAt: z.string().nullable(),
  ageDays: z.number().int().nonnegative().nullable(),
});
export type LocxStockPositionRow = z.infer<typeof LocxStockPositionRowSchema>;

/**
 * `confidencePercent` — of the positions on THIS filtered result set (not
 * just the current page) with a positive balance, the % verified
 * (lastVerifiedAt or, as fallback, mostRecentMovementAt) within the last 30
 * days. 0 when there are no positive-balance positions at all.
 */
export const LocxStockPositionsResponseSchema = z.object({
  items: z.array(LocxStockPositionRowSchema),
  totalCount: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  confidencePercent: z.number().min(0).max(100),
});
export type LocxStockPositionsResponse = z.infer<typeof LocxStockPositionsResponseSchema>;
